// The marker that says: this tab asked to be signed out and the server never confirmed it.
//
// It lives in `localStorage` under its own key rather than inside the `labfront` session record,
// so a reload -- and any sibling tab that is already open -- still honours the logout instead of
// letting a still-live refresh cookie sign the user back in.
//
// Four parties share it: `clearIdentitySession` writes it, the refresh coordinator only READS it
// before each attempt, `createAppStore` reads it at boot and retires it on the reply, and
// `handleSiblingLogoutSaga` hears it being set from another tab and ends this tab's session.
// The in-flight boot logout and its abort controller live here too, because the login sagas
// have to wait on it and nothing else is common to both.
//
// This module imports nothing, including from the `lib` barrel, so it cannot widen the existing
// `lib` <-> `lib/auth` cycle.
//
// It is a correctness aid, not a security control: same-origin script or DevTools can remove it.
export const LOGOUT_PENDING_STORAGE_KEY = 'labfront_logout_pending'

// the stored value is the moment the marker was set, so it also tells one marker from the next
export const logoutPendingValue = (): string | null => {
  try {
    return window.localStorage.getItem(LOGOUT_PENDING_STORAGE_KEY)
  } catch {
    // private-mode storage throws on access; a marker we cannot read is a marker we ignore
    return null
  }
}

export const isLogoutPending = (): boolean => logoutPendingValue() !== null

export const setLogoutPending = (): void => {
  try {
    window.localStorage.setItem(LOGOUT_PENDING_STORAGE_KEY, String(Date.now()))
  } catch {
    // private-mode storage throws on write; the sign-out still has to finish
  }
}

// a logout request can outlive the moment it was fired for. A login abandons the boot logout
// at BOOT_LOGOUT_WAIT_CAP_MS, but nothing else that clears the marker waits on it first, so a hung
// boot logout can still answer after the marker was retired. Every clear bumps this counter. A
// caller that captures it before its request and compares before it writes therefore cannot re-arm
// the marker under a user freshly signed in in THIS tab.
// The counter is module memory, so a login or sign-out in another tab never moves it; a caller
// that must not speak over those compares `logoutPendingValue()` as well.
let generation = 0

export const logoutPendingGeneration = (): number => generation

export const clearLogoutPending = (): void => {
  generation += 1
  try {
    window.localStorage.removeItem(LOGOUT_PENDING_STORAGE_KEY)
  } catch {
    // a marker we cannot remove is one we could not have read either
  }
}

// Which replies mean "the session is really gone", so the marker can go.
//
// 2xx is the logout succeeding. 401 and 404 mean the session was already gone -- the same two
// "session is gone" answers `classifyRefreshResult` treats as terminal, deliberately mirrored so the
// two halves of the session contract cannot drift apart. The refresh side's third terminal status,
// 400, is not mirrored: it says the backend is the wrong version, not that the session ended.
//
// Every other reply keeps the marker, and the next boot tries again. The original design cleared it
// on "any 4xx" too, so that a backend change could never strand a tab that then never refreshes, but any
// 4xx also fails OPEN: a 403 or a 429 is not the server saying the session ended, and clearing on
// one would let the next boot sign the user back in with a still-live refresh cookie.
// Keeping the marker is the safe direction -- a boot that retries a logout it did not need is
// harmless, a boot that skips one it did need is not.
const isDefinitiveLogoutReply = (statusCode: number | null | undefined): boolean =>
  typeof statusCode === 'number' &&
  ((statusCode >= 200 && statusCode < 300) || statusCode === 401 || statusCode === 404)

export const recordLogoutReply = (statusCode: number | null | undefined): void => {
  if (isDefinitiveLogoutReply(statusCode)) {
    clearLogoutPending()
    return
  }
  setLogoutPending()
}

// a login submitted while the boot-time logout is still in flight waits for it, because the
// logout's `Set-Cookie` clearing could otherwise land after the new login's cookie. The cap keeps
// a hung logout from holding the sign-in form hostage, and at the cap the logout is abandoned
// rather than merely no longer waited for: a response the browser never receives cannot apply its
// `Set-Cookie`, and one it received before the abort applied before the login was sent.
export const BOOT_LOGOUT_WAIT_CAP_MS = 3000

interface BootLogout {
  settled: Promise<void>
  controller?: AbortController
}

let bootLogoutInFlight: BootLogout | null = null

// Boot only: `createAppStore` hands over the one best-effort logout it fires, with the controller
// whose signal that request carries. Calling it with nothing in flight (the ordinary boot) is what
// clears a previous boot's record.
export const trackBootLogout = (promise: Promise<unknown> | null, controller?: AbortController): void => {
  if (!promise) {
    bootLogoutInFlight = null
    return
  }

  const bootLogout: BootLogout = {
    settled: promise.then(
      () => undefined,
      () => undefined,
    ),
    controller,
  }
  bootLogoutInFlight = bootLogout
  void bootLogout.settled.then(() => {
    // a later boot logout must not be forgotten by an earlier one finishing
    if (bootLogoutInFlight === bootLogout) bootLogoutInFlight = null
  })
}

// Waits for the boot logout, and abandons it once the cap passes. Returns nothing at all on the
// ordinary boot rather than an already-resolved promise: a caller that `yield call(...)`s this
// must keep running in the same tick when there is nothing to wait for, exactly as it did before
// the wait existed.
export const waitForBootLogout = (): Promise<void> | undefined => {
  const inFlight = bootLogoutInFlight
  if (!inFlight) return undefined

  let timer: ReturnType<typeof setTimeout> | undefined
  return Promise.race([
    inFlight.settled,
    new Promise<void>((resolve) => {
      timer = setTimeout(() => {
        inFlight.controller?.abort()
        resolve()
      }, BOOT_LOGOUT_WAIT_CAP_MS)
    }),
  ]).then(() => {
    if (timer !== undefined) clearTimeout(timer)
  })
}
